import alert from '@/plugins/alert'
import { Attendance, Student, Checkin } from '@/plugins/api'
import { keyBy, rangeRight, chunk } from 'lodash-es'
import moment from 'moment'

export default {
  namespaced: true,
  state: {
    attendances: [],
    // attendances: {},
    pageText: '',
    itemsPerPage: 10,
    searchParams: {},
    totalItems: 0,
    studentAttendances: [],
    date: moment().toISOString(),
    studentCheckins: [],
    loading: false,
    studentDetailAttendances: [],
    attendanceStudentSearchParams: {},
  },
  actions: {
    async getManyAttendances({ commit }, { searchParams, disableSave = false }) {
      if (!disableSave) {
        commit('setAttendances', [])
      }
      commit('changeState', { attendanceStudentSearchParams: searchParams })
      const attendances = await Attendance.fetch(searchParams)
      if (!disableSave) {
        commit('setAttendances', attendances)
      }
      return attendances
    },
    async getAllAttendances({ commit }, { searchParams, disableSave = false }) {
      if (!disableSave) {
        commit('setAttendances', [])
      }
      const attendances = await Attendance.fetch({ ...searchParams, _limit: -1, _start: 0 })
      if (!disableSave) {
        commit('setAttendances', attendances)
      }
      return attendances
    },
    async getAttendanceCount(_, params) {
      const attendanceCount = await Attendance.count(params)
      return attendanceCount
    },

    async searchAttendances({ commit, state, dispatch }, query) {
      const searchParams = {
        _limit: state.itemsPerPage,
        ...query,
      }

      commit('changeState', { searchParams })
      dispatch('requestPageSettings', {})
    },
    async requestPageSettings({ state, commit, dispatch }, { page, itemsPerPage }) {
      state.loading = true
      if (!page) page = 1
      if (!itemsPerPage) itemsPerPage = state.itemsPerPage
      if (state.searchParams) {
        const searchParams = {
          ...state.searchParams,
          _start: (page - 1) * itemsPerPage,
          _limit: itemsPerPage,
          _sort: 'createdAt:DESC',
        }
        const studentSearchParams = {
          _start: (page - 1) * itemsPerPage,
          _limit: itemsPerPage,
          _sort: 'nameToNumber:ASC',
        }
        if (searchParams.grade) studentSearchParams.grade = searchParams.grade.id
        if (searchParams.class) studentSearchParams.currentClass = searchParams.class.id
        if (searchParams.student) studentSearchParams.id = searchParams.student

        const [totalItems, students] = await Promise.all([
          Student.count(studentSearchParams),
          Student.search(studentSearchParams),
        ])

        if (totalItems > (page - 1) * itemsPerPage || page === 1) {
          commit('changeState', {
            totalItems,
            itemsPerPage,
            searchParams,
          })

          const pageStart = (page - 1) * itemsPerPage + 1
          let pageStop = page * itemsPerPage
          pageStop = pageStop > totalItems || pageStop === -1 ? totalItems : pageStop
          const pageText = `${pageStart}-${pageStop} trên ${totalItems}`
          commit('changeState', { pageText })

          // fetch attendance checkin
          let timeGte = null
          let timeLte = null
          if (searchParams && searchParams.time) {
            timeGte = moment(searchParams.time).startOf('day').toISOString()
            timeLte = moment(searchParams.time).endOf('day').toISOString()
          } else {
            timeGte = moment().startOf('day').toISOString()
            timeLte = moment().endOf('day').toISOString()
          }
          let studentCheckins = keyBy(students, 'id')
          const groups = chunk(students, 5)
          for (let subStudents of groups) {
            await Promise.all(
              subStudents.map(async (student) => {
                const checkinObjects = await Checkin.fetch({
                  student: student.id,
                  status: searchParams.status,
                  'attendance.time_gte': timeGte,
                  'attendance.time_lte': timeLte,
                })

                studentCheckins[student.id] = {
                  student,
                  checkinObjects,
                  attendance: checkinObjects[0] && checkinObjects[0].attendance,
                }
              })
            )
          }
          commit('changeState', {
            studentCheckins: Object.values(studentCheckins),
          })
        } else {
          var pages = rangeRight(1, page)
          for (let index = 0; index < pages; index++) {
            const selectedPage = pages[index]
            if (totalItems > (selectedPage - 1) * itemsPerPage) {
              dispatch('requestPageSettings', {})
              break
            }
          }
        }
      }
      state.loading = false
    },

    async fetchAttendances({ commit }, options) {
      commit('setAttendances', await Attendance.fetch(options))
    },
    async fetchStudentAttendances({ commit }, query) {
      const studentParams = {
        status: 'active',
        _limit: -1,
      }
      if (query) {
        if (query.class) studentParams.currentClass = query.class.id
        if (query.grade) studentParams.grade = query.grade
        if (query.student) studentParams.id = query.student
      }
      const students = await Student.fetch(studentParams)

      let timeGte = null
      let timeLte = null
      if (query && query.time) {
        timeGte = moment(query.time).startOf('day').toISOString()
        timeLte = moment(query.time).endOf('day').toISOString()
      } else {
        timeGte = moment().startOf('day').toISOString()
        timeLte = moment().endOf('day').toISOString()
      }

      const attendanceParam = {
        time_gte: timeGte,
        time_lte: timeLte,
        _limit: -1,
      }

      const attendances = await Attendance.fetch(attendanceParam)

      let studentAttendances = students.map((student) => {
        const attendance = attendances.find((a) => a.student.id === student.id)
        return { student, attendance }
      })

      if (query && query.status) {
        if (query.status === 'attendance') studentAttendances = studentAttendances.filter((sa) => sa.attendance)
        else studentAttendances = studentAttendances.filter((sa) => !sa.attendance)
      }

      commit('changeState', { studentAttendances })
    },
    async getAttendanceStudents({ commit, state }, query) {
      state.loading = true
      commit('changeState', { attendanceStudentSearchParams: query })
      const studentCheckins = await Attendance.getAttendanceStudents(query)
      commit('changeState', { studentCheckins })
      state.loading = false
    },
    async checkinAttendance({ commit }, data) {
      const response = await Attendance.checkin(data)
      commit('updateAttendance', response)
    },
    async createAttendance({ commit }, data) {
      try {
        const att = await Attendance.create(data)
        commit('setAttendance', att)
        return att
      } catch (e) {
        alert.error(e)
      }
    },
    async createCheckin(_, data) {
      const response = await Checkin.create(data)
      return response
    },
    async updateAttendance({ commit }, { id, ...attendance }) {
      const response = await Attendance.update(id, attendance)
      commit('updateAttendance', response)
    },
    async updateCheckin(_, { id, ...checkinData }) {
      await Checkin.update(id, checkinData)
    },
    setDate({ commit }, date) {
      commit('changeState', { date })
    },
    async fetchAttendancesInStudentDetail({ commit }, query) {
      var studentDetailAttendances = await Attendance.fetch(query)
      studentDetailAttendances = studentDetailAttendances.map((a) => {
        const at = { ...a, checkinObjects: a.checkins }
        return at
      })
      commit('changeState', { studentDetailAttendances })
    },
  },
  mutations: {
    setAttendances(state, attendances) {
      state.attendances = attendances
    },
    addAttendance(state, attendance) {
      state.attendances.push(attendance)
    },
    updateAttendance(state, attendance) {
      state.attendances = state.attendances.map((s) => {
        if (s.id === attendance.id) {
          return { ...s, ...attendance }
        }
        return s
      })
    },
    // setAttendances(state, attendances) {
    //   state.attendances = attendances.reduce(
    //     (accumulator, currentValue) => ({
    //       ...accumulator,
    //       [currentValue.id]: currentValue
    //     }),
    //     {}
    //   )
    // },
    setAttendance(state, attendance) {
      state.attendances = {
        ...state.attendances,
        [attendance.id]: attendance,
      }
    },
    // updateAttendance(state, attendance) {
    //   const attendances = Object.values(state.attendances).map(a => {
    //     if (attendance.id === a.id) return attendance
    //     else return a
    //   })
    //   state.attendances = keyBy(attendances, 'id')
    // }
  },
  getters: {
    attendances: (state) => {
      return Object.values(state.attendances)
    },
  },
}
